import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMountain,
  faCalendar,
  faVest,
} from "@fortawesome/free-solid-svg-icons";
import {
  sacredFeatures,
  sacredFeaturesByCategory,
  eventSchedule,
  mandatoryGearsBMM,
  getGearIcon,
  CheckCircle,
} from "@mmfestival/shared-components/data/BMMDetail";

export const SectionBMMDetail = ({
  selectedCategory,
  mandatoryGears: propMandatoryGears,
}) => {
  const scheduleItems = eventSchedule;

  const rawCategoryName = selectedCategory?.categoryName || "";

  const normalize = (s = "") =>
    String(s)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]/g, "");

  const findMandatoryKey = (obj, categoryName) => {
    if (!obj || typeof obj !== "object") return null;
    if (!categoryName) return null;
    // direct key match
    if (Object.prototype.hasOwnProperty.call(obj, categoryName))
      return categoryName;

    const keys = Object.keys(obj);
    if (keys.length === 0) return null;
    const lower = categoryName.toLowerCase().trim();

    // exact case-insensitive match
    const keyByLower = keys.find((k) => k.toLowerCase().trim() === lower);
    if (keyByLower) return keyByLower;

    // normalized match
    const norm = normalize(categoryName);
    const keyByNorm = keys.find((k) => normalize(k) === norm);
    if (keyByNorm) return keyByNorm;

    // includes match (e.g. "30K" vs "30K (something)")
    const keyIncludes = keys.find(
      (k) =>
        k.toLowerCase().includes(lower) ||
        lower.includes(k.toLowerCase().trim())
    );
    if (keyIncludes) return keyIncludes;

    return null;
  };

  if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
    try {
      // eslint-disable-next-line no-console
      console.debug(
        "[SectionBMMDetail] mandatoryGearsBMM keys:",
        Object.keys(mandatoryGearsBMM || {})
      );
      // eslint-disable-next-line no-console
      console.debug(
        "[SectionBMMDetail] selectedCategory.categoryName:",
        rawCategoryName
      );
      // eslint-disable-next-line no-console
      console.debug(
        "[SectionBMMDetail] propMandatoryGears present:",
        !!propMandatoryGears
      );
    } catch (e) {
      // ignore
    }
  }

  // Resolve mandatory gears with priority:
  // 1) propMandatoryGears (passed explicitly)
  // 2) selectedCategory.mandatoryGears (from Firestore/Trilogy)
  // 3) mandatoryGearsBMM mapping by category name
  // 4) empty -> hide header/table only
  const mandatoryGearsFromProp =
    Array.isArray(propMandatoryGears) && propMandatoryGears.length
      ? propMandatoryGears
      : null;

  const mandatoryGearsFromCategoryField =
    Array.isArray(selectedCategory?.mandatoryGears) &&
    selectedCategory.mandatoryGears.length
      ? selectedCategory.mandatoryGears
      : null;

  const matchedKey = findMandatoryKey(mandatoryGearsBMM, rawCategoryName);
  const mandatoryGearsFromMapping =
    matchedKey &&
    Array.isArray(mandatoryGearsBMM[matchedKey]) &&
    mandatoryGearsBMM[matchedKey].length
      ? mandatoryGearsBMM[matchedKey]
      : null;

  const effectiveMandatoryGears =
    mandatoryGearsFromProp ||
    mandatoryGearsFromCategoryField ||
    mandatoryGearsFromMapping ||
    [];

  // Only show the mandatory gears header/table if we actually have at least one gear
  const showMandatorySection = effectiveMandatoryGears.length > 0;

  const categoryName = rawCategoryName || (matchedKey || "").toString();

  // Resolve sacred features with same category-matching logic
  const sacredFeaturesKey = findMandatoryKey(sacredFeaturesByCategory, rawCategoryName);
  const effectiveSacredFeatures =
    (sacredFeaturesKey && Array.isArray(sacredFeaturesByCategory[sacredFeaturesKey])
      ? sacredFeaturesByCategory[sacredFeaturesKey]
      : null) ||
    sacredFeatures ||
    [];

  const highlights = effectiveSacredFeatures;

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.4,
        ease: "easeInOut",
      },
    }),
  };

  const leftHighlights = highlights.slice(0, 2);
  const rightHighlights = highlights.slice(2, 4);

  return (
    <motion.div
      className="w-full bg-white py-4"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2, ease: "easeInOut" }}
      viewport={{ once: true }}
    >
      <div className="max-w-7xl 2xl:max-w-[96rem] mx-auto px-4">
        {/* Container Atas - Sacred Features */}
        <motion.div
          className="bg-white mb-8"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon
              icon={faMountain}
              className="text-lg text-cyan-600"
            />
            <h2 className="text-xl lg:text-2xl font-semibold text-slate-700">
              Terrain & Features
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              {leftHighlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  custom={index}
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariants}
                  viewport={{ once: true }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="space-y-4">
              {rightHighlights.map((highlight, index) => (
                <motion.div
                  key={index + 2}
                  custom={index + 2}
                  initial="hidden"
                  whileInView="visible"
                  variants={itemVariants}
                  viewport={{ once: true }}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {highlight.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {highlight.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Container Tengah - Event Schedule */}
        <motion.div
          className="bg-white mb-8"
          initial="hidden"
          whileInView="visible"
          variants={itemVariants}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-3 mb-4">
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-lg text-cyan-600"
            />
            <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">
              Event Schedule
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scheduleItems.map((item, index) => (
              <motion.div
                key={index}
                custom={index}
                initial="hidden"
                whileInView="visible"
                variants={itemVariants}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-4 flex justify-between items-start transition-colors duration-300"
              >
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{item.date}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="font-semibold text-gray-900 text-sm">
                    {item.time}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Render this whole section only if there are gears to show.
            Otherwise hide the header and table, but keep the rest of the component intact. */}
        {showMandatorySection && (
          <motion.div
            className="bg-white"
            initial="hidden"
            whileInView="visible"
            variants={itemVariants}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-4">
              <FontAwesomeIcon
                icon={faVest}
                className="text-lg text-cyan-600"
              />
              <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">
                Mandatory Gears{categoryName ? ` - ${categoryName}` : ""}
              </h2>
            </div>

            <div className="w-full overflow-hidden rounded-2xl border border-gray-300">
              <div className="overflow-x-auto">
                <table className="w-full table-fixed min-w-0">
                  <colgroup>
                    <col className="w-[70%] md:w-auto" />
                    <col className="w-[15%] md:w-auto" />
                    <col className="w-[15%] md:w-auto" />
                  </colgroup>

                  <thead className="border-b border-gray-300">
                    <tr className="bg-gray-100 divide-x divide-gray-300">
                      <th
                        scope="col"
                        className="px-2 sm:px-4 lg:px-20 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-0"
                        aria-label={`Gears ${categoryName}`}
                      >
                        <div className="whitespace-normal">
                          Gears {categoryName}
                        </div>
                      </th>

                      <th
                        scope="col"
                        className="px-1 sm:px-2 lg:px-20 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider"
                        aria-label="Mandatory"
                        title="Mandatory"
                      >
                        <span className="hidden md:inline">Mandatory</span>
                        <span className="inline md:hidden">M</span>
                      </th>

                      <th
                        scope="col"
                        className="px-1 sm:px-2 lg:px-20 py-2 text-center text-xs font-medium text-gray-600 uppercase tracking-wider"
                        aria-label="Recommended"
                        title="Recommended"
                      >
                        <span className="hidden md:inline">Recommended</span>
                        <span className="inline md:hidden">R</span>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-300">
                    {effectiveMandatoryGears.map((gear, index) => (
                      <tr
                        key={`${categoryName}-${index}-${gear.gear || index}`}
                        className="hover:bg-gray-50 divide-x divide-gray-300"
                      >
                        <td className="p-2 sm:p-3 text-sm text-gray-700 min-w-0">
                          <div className="flex items-start gap-2 min-w-0">
                            <img
                              src={getGearIcon(gear.gear || "")}
                              alt={`${gear.gear || "gear"} icon`}
                              className="w-7 h-7 sm:w-8 sm:h-8 object-contain flex-shrink-0 mt-0.5"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = "/gears/default-gear.svg";
                              }}
                            />
                            <span className="break-words whitespace-normal">
                              {gear.gear || ""}
                            </span>
                          </div>
                        </td>

                        <td className="p-1 sm:p-2 text-sm text-gray-700 text-center align-middle">
                          {gear.mandatory ? (
                            <div className="flex justify-center items-center">
                              <CheckCircle />
                            </div>
                          ) : (
                            ""
                          )}
                        </td>

                        <td className="p-1 sm:p-2 text-sm text-gray-700 text-center align-middle">
                          {gear.recommended ? (
                            <div className="flex justify-center items-center">
                              <CheckCircle />
                            </div>
                          ) : (
                            ""
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default SectionBMMDetail;
